import { LocalStorageEncryptionKey } from './local-storage-crypto.util';

// Key 1.0.3 — Secret password lives here in app memory (RAM).
// Starts as null (empty). Set after login or page refresh. Cleared on logout.
// NEVER written to browser localStorage.
const keyState: { encryptionKey: LocalStorageEncryptionKey | null } = {
  encryptionKey: null,
};

// Function to run when password becomes ready — used to convert old plain localStorage data
let onEncryptionKeyReady: (() => void) | null = null;

/**
 * Save a function to run when secret password becomes ready.
 * Parameter handler → comes from installLocalStorageEncryptionShim (handleEncryptionKeyReady)
 */
export function registerLocalStorageEncryptionKeyReadyHandler(
  handler: () => void
): void {
  onEncryptionKeyReady = handler;
}

/**
 * Key 1.0.4 — Get secret password from app memory.
 *
 * No parameters — reads from keyState variable above.
 * Returns password text if user logged in, or null if not logged in yet.
 * Called by: writeEncryptedStorageItem, readEncryptedStorageItem, migratePlainStorageKeys
 */
export function getLocalStorageEncryptionKey(): LocalStorageEncryptionKey | null {
  return keyState.encryptionKey;
}

/**
 * Key 1.0.3 — Save secret password in app memory, or clear on logout.
 *
 * Parameter key → comes from deriveLocalStorageEncryptionKey (Key 1.0.2), or null on logout
 *
 * After saving: if password just became ready, runs onEncryptionKeyReady
 * (converts old plain localStorage data to encrypted).
 */
export function setLocalStorageEncryptionKey(
  key: LocalStorageEncryptionKey | null
): void {
  // Check if password was already ready before this call
  const wasReady = isLocalStorageEncryptionKeyReady();
  // Save new password (or clear with null)
  keyState.encryptionKey = key;

  // Password just became ready for the first time — run waiting function
  if (!wasReady && isLocalStorageEncryptionKeyReady()) {
    onEncryptionKeyReady?.();
  }
}

/**
 * Key 1.0.5 — Check if secret password is ready.
 *
 * Returns true = user logged in, password in memory, encryption can work.
 * Returns false = before login, data saved/read as plain text temporarily.
 */
export function isLocalStorageEncryptionKeyReady(): boolean {
  return keyState.encryptionKey !== null && keyState.encryptionKey.length > 0;
}
